import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';

import type {
  StaffProfileStoresItem,
  StoreProductListItem,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { session } from '@/shared/session/session';

import { storePricingQueryOptions } from './use-store-pricing';
import { storeMembershipQueryOptions } from './use-store-products';

export interface ProductStoreRow {
  store: StaffProfileStoresItem;
  /** The product's row in the store, or null when it is not there; undefined while it loads. */
  row: StoreProductListItem | null | undefined;
  membership: UseQueryResult<StoreProductListItem | null>;
}

export interface ProductStoreRows {
  rows: readonly ProductStoreRow[];
  /** True until every store has answered. */
  isPending: boolean;
}

/** One master product in every store the session may act on. */
export function useProductStoreRows(productId: string): ProductStoreRows {
  const { me } = session.useSession();
  const stores = me?.stores ?? [];
  const memberships = useQueries({
    queries: stores.map((store) => storeMembershipQueryOptions(store.id, productId)),
  });
  const rows = stores.flatMap((store, index) => {
    const membership = memberships[index];
    return membership ? [{ store, membership, row: membership.data }] : [];
  });
  return { rows, isPending: memberships.some((query) => query.isPending) };
}

/** "$1,970.00 – $2,673.40" across what the store sells; null while unknown or nothing is priced. */
export function useStorePriceRange(storeId: string, storeProductId: string | null): string | null {
  const format = useFormat();
  const pricing = useQuery({
    ...storePricingQueryOptions(storeId, storeProductId ?? ''),
    enabled: storeProductId !== null,
  });
  const prices = (pricing.data ?? []).flatMap((row) =>
    row.isAvailable && row.effective ? [row.effective] : [],
  );
  const [first] = prices;
  if (!first) return null;
  const min = prices.reduce((low, price) => (price.amount < low.amount ? price : low), first);
  const max = prices.reduce((high, price) => (price.amount > high.amount ? price : high), first);
  return min.amount === max.amount
    ? format.money(min)
    : `${format.money(min)} – ${format.money(max)}`;
}
